import { useEffect, useState } from "react";
import { toast } from "sonner";

import { adminEndpoints, extractErrorAr } from "@/lib/adminApi";
import Icon from "@/components/Icon";

const TXT_INPUT =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-[#0A1628] focus:outline-none focus:border-[#00E5FF] focus:ring-4 focus:ring-cyan-100 transition-colors";

function fmtSize(bytes) {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch (_) {
    return iso;
  }
}

export default function BackupsAdmin() {
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState([]);
  const [dir, setDir] = useState("");
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyName, setBusyName] = useState("");

  const loadSettings = async () => {
    try {
      const s = await adminEndpoints.getSettings();
      setSettings(s);
    } catch (e) {
      toast.error(extractErrorAr(e));
    }
  };
  const loadList = async () => {
    try {
      const r = await adminEndpoints.listBackups();
      setItems(r.items || []);
      setDir(r.dir || "");
    } catch (e) {
      toast.error(extractErrorAr(e));
    }
  };

  useEffect(() => {
    loadSettings();
    loadList();
  }, []);

  const saveSchedule = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const clean = { ...settings };
      delete clean.wa_access_token_masked;
      const saved = await adminEndpoints.updateSettings(clean);
      setSettings(saved);
      toast.success("تم حفظ إعدادات النسخ الاحتياطي.");
    } catch (e) {
      toast.error(extractErrorAr(e));
    } finally {
      setSaving(false);
    }
  };

  const createNow = async () => {
    setCreating(true);
    const t = toast.loading("جاري إنشاء نسخة احتياطية...");
    try {
      const info = await adminEndpoints.createBackup();
      toast.success(`تم إنشاء ${info.name} (${fmtSize(info.size)})`, { id: t });
      loadSettings();
      loadList();
    } catch (e) {
      toast.error(extractErrorAr(e), { id: t });
    } finally {
      setCreating(false);
    }
  };

  const downloadOne = async (name) => {
    setBusyName(name);
    try {
      await adminEndpoints.downloadBackup(name);
    } catch (e) {
      toast.error(e?.message || "تعذّر تنزيل النسخة.");
    } finally {
      setBusyName("");
    }
  };

  const deleteOne = async (name) => {
    if (!window.confirm(`حذف النسخة "${name}"؟ لا يمكن التراجع.`)) return;
    setBusyName(name);
    try {
      await adminEndpoints.deleteBackup(name);
      toast.success("تم حذف النسخة.");
      loadList();
    } catch (e) {
      toast.error(extractErrorAr(e));
    } finally {
      setBusyName("");
    }
  };

  if (!settings) return <div className="text-slate-500">جاري التحميل...</div>;

  const set = (k, v) => setSettings({ ...settings, [k]: v });

  return (
    <div data-testid="admin-backups">
      <h1 className="font-heading text-3xl font-bold text-[#0A1628]">النسخ الاحتياطي</h1>
      <p className="text-slate-500 mt-1">
        النظام يعمل تلقائياً داخل التطبيق نفسه بدون الحاجة إلى cron خارجي. النسخ تُحفظ محلياً على نفس السيرفر —
        <span className="font-semibold text-[#0A1628]"> يُنصح بشدة بتنزيل نسخة خارجية دورياً وحفظها في مكان آمن. </span>
      </p>

      {/* Schedule */}
      <section className="mt-8 bg-white border border-slate-100 rounded-2xl p-6 space-y-5" data-testid="backup-schedule">
        <h2 className="font-heading text-xl font-bold text-[#0A1628]">الجدولة</h2>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!settings.backup_enabled}
            onChange={(e) => set("backup_enabled", e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-[#00b8cc] focus:ring-[#00E5FF]"
            data-testid="backup-enabled"
          />
          <span className="text-sm font-semibold text-[#0A1628]">تفعيل النسخ الاحتياطي التلقائي</span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">التكرار</label>
            <select
              value={settings.backup_frequency || "daily"}
              onChange={(e) => set("backup_frequency", e.target.value)}
              className={TXT_INPUT}
              data-testid="backup-frequency"
            >
              <option value="daily">يومياً</option>
              <option value="weekly">أسبوعياً</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              عدد النسخ المحتفظ بها
              <span className="block text-xs text-slate-400 font-normal mt-0.5">
                تُحذف الأقدم تلقائياً بعد تجاوز هذا العدد.
              </span>
            </label>
            <input
              type="number"
              min={1}
              max={365}
              value={settings.backup_retention ?? 7}
              onChange={(e) => set("backup_retention", Number(e.target.value))}
              className={TXT_INPUT}
              dir="ltr"
              data-testid="backup-retention"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-slate-500 text-xs">آخر نسخة</div>
            <div className="mt-1 text-[#0A1628] font-semibold" data-testid="backup-last-run">
              {fmtDate(settings.backup_last_run_at)}
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <div className="text-slate-500 text-xs">الحالة</div>
            <div
              className={`mt-1 font-semibold ${
                (settings.backup_last_status || "").startsWith("success")
                  ? "text-emerald-700"
                  : (settings.backup_last_status || "").startsWith("error")
                  ? "text-red-700"
                  : "text-[#0A1628]"
              }`}
              data-testid="backup-last-status"
            >
              {settings.backup_last_status || "—"}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="button"
            onClick={saveSchedule}
            disabled={saving}
            className="aman-btn-light text-sm disabled:opacity-60"
            data-testid="backup-save-schedule"
          >
            <Icon name="send" size={16} strokeWidth={2} />
            {saving ? "جاري الحفظ..." : "حفظ الإعدادات"}
          </button>
          <button
            type="button"
            onClick={createNow}
            disabled={creating}
            className="rounded-full bg-[#0A1628] text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#0f2036] disabled:opacity-60 inline-flex items-center gap-2"
            data-testid="backup-create-now"
          >
            <Icon name="sparkles" size={16} strokeWidth={2} />
            {creating ? "جاري الإنشاء..." : "إنشاء نسخة الآن"}
          </button>
        </div>
      </section>

      {/* List */}
      <section className="mt-8 bg-white border border-slate-100 rounded-2xl p-6" data-testid="backup-list">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold text-[#0A1628]">النسخ الموجودة</h2>
          <div className="text-xs text-slate-500 truncate max-w-[60%]" title={dir}>
            المسار: <span dir="ltr" className="font-mono">{dir}</span>
          </div>
        </div>
        {items.length === 0 ? (
          <div className="text-slate-500 text-sm py-8 text-center">لا توجد نسخ حتى الآن.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-start text-slate-500 border-b border-slate-100">
                  <th className="py-2 px-3 text-start font-medium">الاسم</th>
                  <th className="py-2 px-3 text-start font-medium">التاريخ</th>
                  <th className="py-2 px-3 text-start font-medium">الحجم</th>
                  <th className="py-2 px-3 text-end font-medium">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.name} className="border-b border-slate-50" data-testid={`backup-row-${b.name}`}>
                    <td className="py-3 px-3 font-mono text-xs text-[#0A1628]" dir="ltr">{b.name}</td>
                    <td className="py-3 px-3 text-slate-600">{fmtDate(b.created_at)}</td>
                    <td className="py-3 px-3 text-slate-600" dir="ltr">{fmtSize(b.size)}</td>
                    <td className="py-3 px-3 text-end whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => downloadOne(b.name)}
                        disabled={busyName === b.name}
                        className="text-[#00b8cc] hover:text-[#0A1628] text-xs font-semibold me-3 disabled:opacity-50"
                        data-testid={`backup-download-${b.name}`}
                      >
                        تنزيل
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteOne(b.name)}
                        disabled={busyName === b.name}
                        className="text-red-600 hover:text-red-700 text-xs font-semibold disabled:opacity-50"
                        data-testid={`backup-delete-${b.name}`}
                      >
                        حذف
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
